// Subscribe Fund flow widget — state-aware (Phase 6C).
//
// Variant lookup driven by dossier state + local balance polling:
//
//   currentPhase   balance vs required   Variant
//   ─────────────  ────────────────────  ──────────────────
//   05-signed      < required            fund-instructions
//   05-signed      ≥ required            idle
//   05-signed      mid-tx (local)        pending
//   06-purchased   *                     done
//   ≥ 07-*         *                     historic
//
// On Approve+Send, widget submits two txs via Privy, waits for the
// purchase receipt with viem, then POSTs subscription.purchased. The
// projector's verifier (apps/projector/src/routes/purchase-verify.ts)
// fetches the receipt + decodes the TokensPurchased log + rejects if
// the on-chain reality doesn't match. After 200, refetchKey bumps and
// state.current re-reads to flip the variant.

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Address, Button, ConnectionDot, Money, getExplorerUrl } from '@rmint/ui';
import { useAuth } from '@rmint/shell';
import { useSubscriptionState } from '../../lib/use-subscription-state';
import { useOffering, type Offering } from '../../lib/offering';
import { useInvestorSafe } from '../../lib/safe';
import { fetchAllocationCapUsd } from '../../lib/investor';
import {
  publicClient,
  useUsdcBalance,
  useUsdcAllowance,
  useApproveUsdc,
  usePurchase,
} from '../../lib/onchain';
import { env } from '@rmint/shell';
import type { WidgetProps } from './registry';

const FALLBACK_OFFERING: Offering = {
  rmintId: 'sakura-ramen-test-d1',
  restaurantName: 'Sakura Ramen NC',
  ticker: 'RMOT-SAKURA-D1',
  tokenContractAddress: '0xB03e42d3B2F86343dEcC3631bBd792F7f988eCAd',
  saleContractAddress: '0xf8FF966494E91283Ca7b744c60F12811C6bD92dC',
  pricePerToken: 1.0,
  minAmountUsdc: 1000,
  maxAmountUsdc: 50000,
  lockupMonths: 36,
  lockupEndIso: '2029-05-09T00:00:00Z',
  signByDeadlineIso: '2026-06-09T00:00:00Z',
  estimatedFirstDistributionIso: '2026-08-15T00:00:00Z',
  networkFeeUsdcEst: 2.5,
};

export type Variant = 'fund-instructions' | 'idle' | 'pending' | 'done' | 'historic';

const DONE_PHASES = new Set(['06-purchased']);
const HISTORIC_PHASES = new Set(['07-holding', '08-settled', '09-refunded', '10-kyc-rejected']);

// Exported for unit testing — pure function, no React.
export function selectFundVariant(
  currentPhase: string | undefined,
  balanceMicro: bigint | null,
  requiredMicro: bigint,
  localTxState: 'idle' | 'pending',
): Variant {
  if (!currentPhase) return 'idle';
  if (DONE_PHASES.has(currentPhase)) return 'done';
  if (HISTORIC_PHASES.has(currentPhase)) return 'historic';
  if (currentPhase !== '05-signed') return 'idle';
  if (localTxState === 'pending') return 'pending';
  if (balanceMicro !== null && balanceMicro < requiredMicro) return 'fund-instructions';
  return 'idle';
}

export function SubscribeFundFlow(props: WidgetProps) {
  const navigate = useNavigate();
  const { walletAddress, privyDid } = useAuth();
  const subscriptionId =
    (props.subscriptionId as string | undefined) ??
    (props.subscriptionid as string | undefined) ??
    '';

  const debugStateOverride = props.state as string | undefined;

  const [refetchKey, setRefetchKey] = useState(0);
  const sub = useSubscriptionState(subscriptionId, refetchKey);

  const dossierRmintId = (sub.state?.raw.data as { rmintId?: string } | undefined)?.rmintId;
  const propRmintId = props.rmintId as string | undefined;
  const rmintId = dossierRmintId ?? propRmintId;
  const off = useOffering(rmintId);
  const offering: Offering = off.offering ?? FALLBACK_OFFERING;

  const safeHook = useInvestorSafe(privyDid);

  const subData = sub.state?.raw.data as { investorId?: string; capUsd?: number } | undefined;

  // Fix A (CLEANUP_2026-05-11): the projector's state.current materializer
  // REPLACES `data` per event, so post-sign the allocation amount
  // (capUsd) is no longer in subData. Read from lifecycle/04-allocated/
  // allocation-issued.yaml directly as a fallback so the Fund step can
  // proceed. Long-term Fix B reworks the materializer to ACCUMULATE
  // — see docs/plan/STATE_CURRENT_ACCUMULATION_PLAN.md.
  const [allocationCapUsd, setAllocationCapUsd] = useState<number | null>(null);
  useEffect(() => {
    if (!subscriptionId) return;
    let cancelled = false;
    (async () => {
      try {
        const cap = await fetchAllocationCapUsd(subscriptionId);
        if (!cancelled) setAllocationCapUsd(cap);
      } catch {
        // Network / ACL failure — leave null so we fall through to subData.capUsd
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [subscriptionId]);

  const capUsd = subData?.capUsd ?? allocationCapUsd ?? 0;
  const requiredMicro = useMemo(() => BigInt(capUsd) * 1_000_000n, [capUsd]);

  // useUsdcBalance returns AsyncResult<{balance: bigint; decimals: number}>
  const balanceHook = useUsdcBalance(walletAddress as `0x${string}` | undefined);
  const balanceMicro: bigint | null = balanceHook.data?.balance ?? null;

  const allowanceHook = useUsdcAllowance(
    walletAddress as `0x${string}` | undefined,
    offering.saleContractAddress,
    env.chainEnv,
  );

  const approve = useApproveUsdc(
    offering.saleContractAddress,
    requiredMicro,
    env.chainEnv,
  );
  const purchase = usePurchase(
    offering.saleContractAddress,
    requiredMicro,
    safeHook.safe?.safeAddress ?? '0x0000000000000000000000000000000000000000',
  );

  const [localTxState, setLocalTxState] = useState<'idle' | 'pending'>('idle');
  const [pendingLabel, setPendingLabel] = useState<string>('');
  const [pendingTxHash, setPendingTxHash] = useState<`0x${string}` | null>(null);
  const [flowError, setFlowError] = useState<string | null>(null);
  const [needsApprove, setNeedsApprove] = useState(true);

  // Recheck allowance: if user already approved enough in a previous
  // session, skip approve.
  useEffect(() => {
    if (allowanceHook.allowance !== null) {
      setNeedsApprove(allowanceHook.allowance < requiredMicro);
    }
  }, [allowanceHook.allowance, requiredMicro]);

  const dossierVariant = selectFundVariant(
    sub.state?.currentPhase,
    balanceMicro,
    requiredMicro,
    localTxState,
  );
  const overrideVariant: Variant | null =
    debugStateOverride === 'fund-instructions'
      ? 'fund-instructions'
      : debugStateOverride === 'pending'
        ? 'pending'
        : debugStateOverride === 'done'
          ? 'done'
          : debugStateOverride === 'historic'
            ? 'historic'
            : null;
  const variant: Variant = overrideVariant ?? dossierVariant;

  // I2 fix: poll USDC balance while showing fund-instructions card so the
  // variant auto-flips once the user funds their wallet. Cache TTL is 15s,
  // so polling more frequently is wasted; ALSO must ONLY run for the
  // instructions variant (avoid waste on idle/done renders).
  useEffect(() => {
    if (variant !== 'fund-instructions') return;
    if (!balanceHook.refresh) return;
    const id = setInterval(() => balanceHook.refresh(), 15_000);
    return () => clearInterval(id);
  }, [variant, balanceHook.refresh]);

  async function handleApproveAndPurchase() {
    if (!walletAddress || !subscriptionId || !rmintId || !subData) {
      setFlowError('Missing context — reload and try again.');
      return;
    }
    if (!safeHook.safe) {
      setFlowError("Your custody Safe isn't provisioned yet — contact support.");
      return;
    }
    setLocalTxState('pending');
    setFlowError(null);

    // Diagnostic instrumentation (tagged [subscribe-fund]) — added to
    // catch the actual failure mode on the next sepolia run. The
    // "didn't auto-POST" symptom from 2026-05-11 needs evidence: was
    // it a hash-format issue, a receipt timeout, a verifier rejection,
    // or a Privy-modal dismissal hang? Strip these once we have logs.
    // See docs/plan/SMART_ACCOUNT_ONBOARDING_PLAN.md "Postscript".
    console.log('[subscribe-fund] enter', { subscriptionId, needsApprove, capUsd });
    try {
      // Step 1: approve (skip if allowance already sufficient)
      if (needsApprove) {
        setPendingLabel('Approving USDC');
        const approveHash = await approve.send();
        console.log('[subscribe-fund] approve.send returned', approveHash);
        setPendingTxHash(approveHash);
        await publicClient.waitForTransactionReceipt({ hash: approveHash });
        console.log('[subscribe-fund] approve receipt confirmed');
      }

      // Step 2: purchase
      setPendingLabel('Sending USDC to escrow');
      const purchaseHash = await purchase.send();
      console.log('[subscribe-fund] purchase.send returned', purchaseHash);
      setPendingTxHash(purchaseHash);
      const receipt = await publicClient.waitForTransactionReceipt({ hash: purchaseHash });
      console.log('[subscribe-fund] purchase receipt', {
        blockNumber: receipt.blockNumber,
        to: receipt.to,
        from: receipt.from,
        status: receipt.status,
        logCount: receipt.logs.length,
      });

      // Step 3: POST subscription.purchased (with retry on tx-not-found)
      setPendingLabel('Verifying transaction');
      const tokens = Math.floor(capUsd / offering.pricePerToken);
      const body = {
        entityType: 'subscription',
        entityId: subscriptionId,
        eventId: `evt_purchased_${crypto.randomUUID()}`,
        eventType: 'subscription.purchased',
        occurredAt: new Date().toISOString(),
        body: {
          subject: { subscriptionId, investorId: subData.investorId ?? 'inv_unknown', rmintId },
          data: {
            txHash: purchaseHash,
            blockNumber: Number(receipt.blockNumber),
            amountUsdc: capUsd,
            rotAmount: tokens,
            chainId: env.chainEnv === 'base' ? 8453 : env.chainEnv === 'local' ? 31337 : 84532,
          },
        },
      };

      let postOk = false;
      let lastError: string | null = null;
      for (let attempt = 0; attempt < 3 && !postOk; attempt += 1) {
        if (attempt > 0) await new Promise((r) => setTimeout(r, 3000));
        console.log('[subscribe-fund] POST /events attempt', attempt);
        const res = await fetch('/events', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          postOk = true;
          console.log('[subscribe-fund] POST /events ok', res.status);
        } else {
          const err = (await res.json().catch(() => ({}))) as { error?: { kind?: string } };
          lastError = `${res.status} ${err.error?.kind ?? 'unknown'}`;
          console.log('[subscribe-fund] POST /events failed', { status: res.status, kind: err.error?.kind });
          if (err.error?.kind !== 'tx-not-found') break;
        }
      }
      if (!postOk) {
        throw new Error(`purchase verification failed: ${lastError}`);
      }

      setLocalTxState('idle');
      setPendingTxHash(null);
      setRefetchKey((k) => k + 1);
    } catch (e) {
      console.log('[subscribe-fund] flow caught error', e);
      setFlowError((e as Error).message);
      setLocalTxState('idle');
    }
  }

  // ─── Render branches ──────────────────────────────────────────────

  if (variant === 'done') {
    return (
      <div className="rounded-card border border-success-line bg-success-bg p-6 space-y-3">
        <output className="font-bold text-success">USDC sent to escrow ✓</output>
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate(`/subscribe/${encodeURIComponent(subscriptionId)}/mint`)}
          >
            Continue to Mint →
          </Button>
        </div>
      </div>
    );
  }

  if (variant === 'historic') {
    return (
      <div className="space-y-4">
        <div className="rounded-card border border-border bg-card p-4 space-y-1 text-[13px]">
          <p className="font-semibold text-ink">USDC purchased</p>
          <p className="text-[11px] text-foreground-mute">This subscription is past the purchase step.</p>
        </div>
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate(`/subscribe/${encodeURIComponent(subscriptionId)}/mint`)}
          >
            Continue to Mint →
          </Button>
        </div>
      </div>
    );
  }

  if (variant === 'pending') {
    return (
      <div className="space-y-3">
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-3 px-4 py-3 rounded-card border border-border bg-card-warm"
        >
          <div className="w-6 h-6 border-2 border-border-strong border-t-primary rounded-full animate-spin" aria-hidden />
          <div className="flex-1">
            <output className="font-bold text-[13px]">{pendingLabel}…</output>
            <div className="text-[12px] text-foreground-mute">Waiting for confirmation</div>
          </div>
          {pendingTxHash ? (
            <a
              href={getExplorerUrl(env.chainEnv, 'tx', pendingTxHash) ?? '#'}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-[11px] text-primary hover:underline"
            >
              {pendingTxHash.slice(0, 6)}…{pendingTxHash.slice(-4)} ↗
            </a>
          ) : null}
        </div>
        {flowError ? (
          <div role="alert" className="rounded-card border border-warn-line bg-warn-bg px-4 py-3 text-[13px] text-warn">{flowError}</div>
        ) : null}
      </div>
    );
  }

  if (variant === 'fund-instructions') {
    return (
      <div className="space-y-4">
        <div className="rounded-card border border-warn-line bg-warn-bg px-4 py-3 text-[13px]">
          <div className="font-semibold mb-1">Insufficient USDC balance</div>
          <div>
            Your wallet needs <Money value={capUsd} unit="USDC" /> to complete this purchase.
            Current balance: <Money value={Number((balanceMicro ?? 0n) / 1_000_000n)} unit="USDC" />.
          </div>
        </div>
        <div className="rounded-card border border-border bg-card p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-wide text-foreground-mute">Send USDC to your wallet</div>
          {walletAddress ? (
            <Address value={walletAddress as `0x${string}`} variant="full" status="connected" label="Your wallet" />
          ) : null}
          <p className="text-[12px] text-foreground-mute mt-2">
            Buy USDC on{' '}
            <a href="https://www.coinbase.com" target="_blank" rel="noreferrer" className="text-primary hover:underline">
              Coinbase
            </a>
            ,{' '}
            <a href="https://www.kraken.com" target="_blank" rel="noreferrer" className="text-primary hover:underline">
              Kraken
            </a>
            , or any exchange that supports the Base network. Send to the address above. Balance refreshes every 15 seconds; this card flips to the order summary once funds arrive.
          </p>
        </div>
      </div>
    );
  }

  // idle — order summary + Approve/Send CTA
  if (!safeHook.loading && !safeHook.safe) {
    return (
      <div className="rounded-card border border-warn-line bg-warn-bg px-4 py-3 text-[13px] text-warn">
        Your custody Safe isn't provisioned yet — contact support.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 px-4 py-3 rounded-card border border-border bg-card">
        <ConnectionDot />
        <div className="flex-1">
          <div className="text-[11px] text-foreground-mute">Sending from</div>
          {walletAddress ? (
            <Address value={walletAddress as `0x${string}`} variant="elided" />
          ) : (
            <span className="text-xs text-foreground-mute">no wallet connected</span>
          )}
        </div>
        <div className="text-right">
          <div className="font-mono text-[13px] font-bold">
            <Money value={Number((balanceMicro ?? 0n) / 1_000_000n)} unit="USDC" />
          </div>
          <div className="text-[11px] text-foreground-mute">balance on Base</div>
        </div>
      </div>

      <dl className="rounded-card border border-border bg-card-warm p-4 grid grid-cols-[1fr_auto] gap-y-2 text-[13px]">
        <dt className="text-foreground-mute">Subscription amount</dt>
        <dd className="font-mono text-right m-0"><Money value={capUsd} /></dd>
        <dt className="text-foreground-mute">Network fee (est.)</dt>
        <dd className="font-mono text-right m-0"><Money value={offering.networkFeeUsdcEst} /></dd>
        <dt className="text-foreground-mute border-t border-border-soft pt-2 font-bold">Total</dt>
        <dd className="font-mono text-right border-t border-border-soft pt-2 font-bold m-0">
          <output><Money value={capUsd + offering.networkFeeUsdcEst} /></output>
        </dd>
      </dl>

      <aside role="note" className="flex gap-3 px-4 py-3 rounded-card border border-info-line bg-info-bg text-[12px] leading-snug text-ink">
        <span aria-hidden className="inline-flex w-5 h-5 shrink-0 items-center justify-center rounded-full bg-info text-white font-mono text-[13px] font-bold">i</span>
        <div>
          <strong>{needsApprove ? 'Two transactions:' : 'One transaction:'}</strong>{' '}
          {needsApprove
            ? 'first approve RMINT to spend USDC (no funds move), then confirm transfer to escrow. Your wallet will prompt you twice.'
            : 'allowance already approved; one tx will send USDC to escrow.'}
        </div>
      </aside>

      {flowError ? (
        <div className="rounded-card border border-warn-line bg-warn-bg px-4 py-3 text-[13px] text-warn">{flowError}</div>
      ) : null}

      <div className="flex justify-end">
        <Button
          variant="primary"
          size="lg"
          onClick={handleApproveAndPurchase}
          disabled={
            !walletAddress ||
            sub.loading ||
            off.loading ||
            !off.offering ||
            safeHook.loading ||
            !safeHook.safe ||
            balanceHook.loading ||
            balanceMicro === null ||
            balanceMicro < requiredMicro
          }
        >
          {needsApprove ? 'Approve USDC in wallet →' : 'Send USDC to escrow →'}
        </Button>
      </div>
    </div>
  );
}
